import React, { useEffect, VFC } from 'react';
import { useParams } from 'react-router-dom';
import { useSessionStore } from 'store/session';
import { useInventoryLocationBuildings } from 'hooks/api/inventory-location-building/useFind';
import { StepsProgressBar } from 'components/progress/ProgressBar';
import { useIntl } from 'react-intl';
import { Layout } from '../../components/Layout/Layout';
import { renderContentByStep } from './renderContentByStep';
import { renderFooterByStep } from './renderFooterByStep';
import { selectInventorizeFormFromPayload, useStore } from './useStore';
import { INVENTORIZE_STEP_TRANSLATED, INVENTORIZE_STEPS_COUNT, useInventorizeStepsStore } from './components/store';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { MeterContext } from '../../contexts/Meter';
import { InventorizeMeterPage as messages } from './messages';
import { useContextMeter } from '../../hooks/useContextMeter';

export const InventorizeMeterPage: VFC = () => {
  const intl = useIntl();
  const { id: inventoryMeterId } = useParams();
  const { apiKey } = useSessionStore();
  const { currentStep } = useInventorizeStepsStore();
  const { setForm, setLocationBuildings } = useStore();
  const toolsId = parseInt(inventoryMeterId ?? '');
  const { enmonMeter, inventoryMeter, isLoading } = useContextMeter(toolsId);

  useEffect(() => {
    if (enmonMeter && inventoryMeter) {
      setForm(selectInventorizeFormFromPayload(enmonMeter, inventoryMeter));
    }
  }, [setForm, enmonMeter, inventoryMeter]);

  // Fetch location buildings for useStore
  useInventoryLocationBuildings(
    { onSuccess: setLocationBuildings, errorMessage: intl.formatMessage(messages.apiKeyErrorMessage) },
    { enabled: !!apiKey },
  );

  const PageContent = renderContentByStep(currentStep);
  const PageFooter = renderFooterByStep(currentStep);

  const contextValue = React.useMemo(
    () => ({ enmonMeter, inventoryMeter, isLoading }),
    [enmonMeter, inventoryMeter, isLoading],
  );

  return (
    <MeterContext.Provider value={contextValue}>
      <Layout
        footerContent={<PageFooter />}
        headerContent={intl.formatMessage(INVENTORIZE_STEP_TRANSLATED[currentStep])}
        progressBarContent={<StepsProgressBar progressStep={currentStep} steps={INVENTORIZE_STEPS_COUNT} />}
      >
        {isLoading ? (
          <LoadingSpinner message={intl.formatMessage(messages.loadingMeter)} />
        ) : (
          <div style={{ height: '100%' }}>
            <PageContent key={toolsId ?? ''} />
          </div>
        )}
      </Layout>
    </MeterContext.Provider>
  );
};
